import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase, updateProfile } from '../lib/supabase'
import { useToast } from '../lib/useToast'
import './CivicPreferences.css'

const CIVIC_TOPICS = [
  { id: 'zoning',       label: 'Zoning',        icon: '◈', desc: 'Land use, housing, development projects' },
  { id: 'schools',      label: 'Schools',        icon: '◎', desc: 'School board, education funding, curriculum' },
  { id: 'budget',       label: 'Budget',         icon: '◇', desc: 'City spending, taxes, fiscal policy' },
  { id: 'police',       label: 'Public Safety',  icon: '⬡', desc: 'Police, fire, emergency services' },
  { id: 'environment',  label: 'Environment',    icon: '◉', desc: 'Parks, water, sustainability, climate' }
]

export default function CivicPreferences() {
  const { user, profile, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const { toast, showToast } = useToast()

  const [cities, setCities] = useState([])
  const [cityInput, setCityInput] = useState('')
  const [selectedTopics, setSelectedTopics] = useState([])
  const [saving, setSaving] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [notifsLoading, setNotifsLoading] = useState(false)

  useEffect(() => {
    if (!user) { navigate('/'); return }
    if (profile) {
      setCities(profile.civic_cities || [])
      setSelectedTopics(profile.civic_topics || [])
    }
    loadNotifications()
  }, [user, profile]) // eslint-disable-line

  const loadNotifications = async () => {
    if (!user) return
    setNotifsLoading(true)
    try {
      const { data } = await supabase
        .from('civic_notifications')
        .select('*, civic_events(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20)
      setNotifications(data || [])
    } catch (_) {}
    finally { setNotifsLoading(false) }
  }

  const addCity = () => {
    const trimmed = cityInput.trim()
    if (!trimmed) return
    if (cities.includes(trimmed)) {
      showToast('City already added')
      return
    }
    setCities(prev => [...prev, trimmed])
    setCityInput('')
  }

  const removeCity = (city) => {
    setCities(prev => prev.filter(c => c !== city))
  }

  const toggleTopic = (topicId) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(t => t !== topicId)
        : [...prev, topicId]
    )
  }

  const handleSave = async () => {
    if (!user) return
    setSaving(true)
    try {
      await updateProfile(user.id, {
        civic_cities: cities,
        civic_topics: selectedTopics
      })
      await refreshProfile()
      showToast('✓ Civic preferences saved')
    } catch (e) {
      showToast('Failed to save: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const markAllRead = async () => {
    if (!user) return
    try {
      await supabase
        .from('civic_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    } catch (_) {}
  }

  const unreadCount = notifications.filter(n => !n.is_read).length

  return (
    <div className="civpref-page">
      {/* Header */}
      <header className="civpref-header">
        <button className="civpref-back-btn" onClick={() => navigate('/profile')}>← Profile</button>
        <div className="civpref-header-title">Civic Preferences</div>
      </header>

      <div className="civpref-layout">

        {/* ── Cities ── */}
        <section className="civpref-section">
          <div className="civpref-section-title">Your Cities</div>
          <div className="civpref-section-sub">
            Get notified about civic events in these cities.
          </div>

          <div className="civpref-city-input-row">
            <input
              className="civpref-input"
              placeholder="City name (e.g. Austin)"
              value={cityInput}
              onChange={e => setCityInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCity()}
            />
            <button className="btn btn-primary civpref-add-btn" onClick={addCity}>Add</button>
          </div>

          {cities.length > 0 ? (
            <div className="civpref-city-chips">
              {cities.map(city => (
                <div key={city} className="civpref-city-chip">
                  <span>{city}</span>
                  <button
                    className="civpref-city-remove"
                    onClick={() => removeCity(city)}
                  >×</button>
                </div>
              ))}
            </div>
          ) : (
            <div className="civpref-empty-hint">No cities added yet. Add at least one to receive notifications.</div>
          )}
        </section>

        {/* ── Topics ── */}
        <section className="civpref-section">
          <div className="civpref-section-title">Topics You Care About</div>
          <div className="civpref-section-sub">
            You'll be notified when new civic events match these topics in your cities.
          </div>
          <div className="civpref-topics-grid">
            {CIVIC_TOPICS.map(topic => (
              <button
                key={topic.id}
                className={`civpref-topic-card ${selectedTopics.includes(topic.id) ? 'selected' : ''}`}
                onClick={() => toggleTopic(topic.id)}
              >
                <span className="civpref-topic-icon">{topic.icon}</span>
                <span className="civpref-topic-label">{topic.label}</span>
                <span className="civpref-topic-desc">{topic.desc}</span>
                {selectedTopics.includes(topic.id) && (
                  <span className="civpref-topic-check">✓</span>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* ── Save button ── */}
        <div className="civpref-save-row">
          <button
            className="btn btn-primary civpref-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>

        {/* ── Civic Notifications ── */}
        <section className="civpref-section">
          <div className="civpref-notifs-header">
            <div>
              <div className="civpref-section-title">
                Civic Notifications
                {unreadCount > 0 && (
                  <span className="civpref-unread-badge">{unreadCount}</span>
                )}
              </div>
              <div className="civpref-section-sub">Events that matched your city and topic preferences.</div>
            </div>
            {unreadCount > 0 && (
              <button className="civpref-mark-read-btn" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {notifsLoading ? (
            <div className="civpref-loading"><span className="spinner" /></div>
          ) : notifications.length === 0 ? (
            <div className="civpref-empty-hint">
              No notifications yet. Save your preferences and check back after new civic events are processed.
            </div>
          ) : (
            <div className="civpref-notif-list">
              {notifications.map(n => {
                const event = n.civic_events
                if (!event) return null
                return (
                  <div
                    key={n.id}
                    className={`civpref-notif-row ${!n.is_read ? 'civpref-notif-unread' : ''}`}
                    onClick={() => navigate(`/feed?civic_tab=true`)}
                  >
                    <div className="civpref-notif-dot" />
                    <div className="civpref-notif-content">
                      <div className="civpref-notif-title">{event.title}</div>
                      <div className="civpref-notif-meta">
                        {event.city}{event.state ? `, ${event.state}` : ''} · {event.topic_tag} ·{' '}
                        {new Date(n.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </div>

      {/* Toast */}
      <div className={`toast ${toast.visible ? 'show' : ''} ${toast.type}`}>
        {toast.message}
      </div>
    </div>
  )
}
